import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, CircleNotch, CaretUp, CaretDown } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SortIcon({ k, sortKey, sortDir }) {
  if (sortKey !== k) return null;
  return sortDir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />;
}

function SpreadTable({ rows, type, data }) {
  const [sortKey, setSortKey] = useState(type === 'spread' ? 'spread' : 'diff');
  const [sortDir, setSortDir] = useState('asc');

  const onSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    const s = [...rows];
    s.sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return s;
  }, [rows, sortKey, sortDir]);

  const mon = data?.current_month_abbr || 'Apr';
  const cy = data?.current_year || 2026;
  const yr1 = data?.yr1 || 2023;
  const yr2 = data?.yr2 || 2024;
  const yr3 = data?.yr3 || 2025;
  const hCls = "px-1 py-1.5 text-[10px] font-bold text-gray-600 cursor-pointer select-none whitespace-nowrap";
  const isSpread = type === 'spread';
  const label = isSpread ? 'Sprd' : 'Diff';
  const primaryKey = isSpread ? 'spread' : 'diff';

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] border-collapse">
        <thead className="sticky top-0 z-10 bg-white">
          <tr>
            <th onClick={() => onSort(primaryKey)} className={hCls}>{label} <SortIcon k={primaryKey} sortKey={sortKey} sortDir={sortDir} /></th>
            {!isSpread && <th onClick={() => onSort('predicted_spread')} className={`${hCls} bg-amber-200`}>Pred <SortIcon k="predicted_spread" sortKey={sortKey} sortDir={sortDir} /></th>}
            <th onClick={() => onSort('times')} className={`${hCls} bg-gray-200`}>Times <SortIcon k="times" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('overall_pct')} className={hCls}>% <SortIcon k="overall_pct" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('month_count')} className={`${hCls} bg-amber-100`}>{mon} <SortIcon k="month_count" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('month_pct')} className={`${hCls} bg-amber-100`}>{mon}% <SortIcon k="month_pct" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('diff_pct')} className={`${hCls} bg-red-100`}>D% <SortIcon k="diff_pct" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('y1')} className={`${hCls} bg-blue-100`}>{mon}{String(yr1).slice(2)} <SortIcon k="y1" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('y2')} className={`${hCls} bg-blue-100`}>{mon}{String(yr2).slice(2)} <SortIcon k="y2" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('y3')} className={`${hCls} bg-blue-100`}>{mon}{String(yr3).slice(2)} <SortIcon k="y3" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('jfm_cy')} className={`${hCls} bg-teal-100`}>JFM{String(cy).slice(2)} <SortIcon k="jfm_cy" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('cy_month')} className={`${hCls} bg-green-100`}>{mon}{String(cy).slice(2)} <SortIcon k="cy_month" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('cy_overall')} className={`${hCls} bg-purple-100`}>Tot <SortIcon k="cy_overall" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('absent_years')} className={`${hCls} bg-red-100`}>Abs <SortIcon k="absent_years" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('gap')} className={`${hCls} bg-orange-100`}>Gap <SortIcon k="gap" sortKey={sortKey} sortDir={sortDir} /></th>
            <th onClick={() => onSort('hot_cold')} className={`${hCls} bg-rose-100`}>H/C <SortIcon k="hot_cold" sortKey={sortKey} sortDir={sortDir} /></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, idx) => {
            const val = isSpread ? row.spread : row.diff;
            const isImpossible = !isSpread && row.is_impossible;
            return (
              <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${isImpossible ? 'opacity-30' : ''}`}>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono font-bold text-center ${isImpossible ? 'text-red-400 line-through' : isSpread ? 'text-blue-700' : val > 0 ? 'text-green-700' : val < 0 ? 'text-red-600' : 'text-blue-700'}`}>
                  {isSpread ? val : (val > 0 ? '+' : '')}{isSpread ? '' : val}
                </td>
                {!isSpread && (
                  <td className="px-1 py-1 border-b border-gray-100 text-center">
                    {row.predicted_spread != null && !isImpossible ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-200 text-gray-900 font-bold text-xs">{row.predicted_spread}</span>
                    ) : <span className="text-gray-300">-</span>}
                  </td>
                )}
                <td className="px-1 py-1 border-b border-gray-100 font-mono text-center font-bold bg-gray-50">{row.times}</td>
                <td className="px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px]">{(row.overall_pct * 100).toFixed(1)}%</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-amber-50/40 ${row.month_count > 0 ? 'font-bold' : 'text-gray-300'}`}>{row.month_count || '-'}</td>
                <td className="px-1 py-1 border-b border-gray-100 font-mono text-center bg-amber-50/40 text-[10px]">{(row.month_pct * 100).toFixed(1)}%</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] font-bold ${row.diff_pct > 0 ? 'text-green-700' : row.diff_pct < 0 ? 'text-red-600' : 'text-gray-400'}`}>{(row.diff_pct * 100) > 0 ? '+' : ''}{(row.diff_pct * 100).toFixed(1)}%</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-blue-50/30 ${row.y1 > 0 ? '' : 'text-gray-300'}`}>{row.y1 || '-'}</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-blue-50/30 ${row.y2 > 0 ? '' : 'text-gray-300'}`}>{row.y2 || '-'}</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-blue-50/30 ${row.y3 > 0 ? '' : 'text-gray-300'}`}>{row.y3 || '-'}</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-teal-50/40 font-bold ${row.jfm_cy > 0 ? '' : 'text-gray-300'}`}>{row.jfm_cy || '-'}</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-green-50/30 font-bold ${row.cy_month > 0 ? '' : 'text-gray-300'}`}>{row.cy_month || '-'}</td>
                <td className="px-1 py-1 border-b border-gray-100 font-mono text-center font-bold bg-purple-50/30">{row.cy_overall || '-'}</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] font-bold ${row.absent_years >= 3 ? 'text-red-600' : row.absent_years > 0 ? 'text-orange-600' : 'text-gray-400'}`}>{row.absent_years}yr</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] ${row.gap >= 10 ? 'text-red-600 font-bold' : ''}`}>{row.gap}</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] font-bold ${row.hot_cold > 0.02 ? 'text-red-600' : row.hot_cold < -0.02 ? 'text-blue-600' : 'text-gray-400'}`}>{row.hot_cold > 0 ? '+' : ''}{(row.hot_cold * 100).toFixed(1)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Window43LSSpreadDiff() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try { const res = await axios.get(`${API}/analysis/ls-spread-diff`); setData(res.data); }
    catch { if (!silent) toast.error('Failed to fetch data'); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100" data-testid="w43-page">
      <Toaster position="top-right" />
      <header className="bg-gradient-to-r from-[#002FA7] to-[#0040DD] px-6 py-4 flex items-center gap-4 shadow-lg">
        <button onClick={() => navigate('/')} className="text-white/70 hover:text-white" data-testid="back-btn"><ArrowLeft size={22} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>W36: LS2 - LS1 Spread Analysis</h1>
          <p className="text-xs text-blue-200 mt-0.5">Spread frequency, spread-to-spread differences, and trend analysis</p>
        </div>
        {data && (
          <div className="flex items-center gap-3">
            <div className="bg-white/10 rounded-lg px-3 py-1.5 text-white text-xs">
              Last: LS1=<span className="font-bold text-amber-300">{data.last_draw.ls1}</span>
              {' '}LS2=<span className="font-bold text-amber-300">{data.last_draw.ls2}</span>
              {' '}Spread=<span className="font-bold text-red-300">{data.last_draw.spread}</span>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-[1600px] mx-auto p-6">


        <WindowNarrative windowId="W36" {...NARRATIVES.W36} className="mb-5" />
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={40} className="animate-spin text-blue-600" /></div>
        ) : data ? (
          <div className="space-y-6">
            {/* Recent Spreads */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-2">Last 20 Spreads (LS2 - LS1)</h3>
              <div className="flex gap-1 flex-wrap items-center">
                {data.recent_spreads?.map((s, i) => (
                  <div key={i} className="flex items-center gap-0.5" data-testid={`recent-spread-${i}`}>
                    <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-xs ${i === data.recent_spreads.length - 1 ? 'bg-red-600 text-white ring-2 ring-red-300' : 'bg-gray-200 text-gray-700'}`}>{s.spread}</span>
                    {i < data.recent_spreads.length - 1 && (
                      <span className={`text-[8px] font-bold ${data.recent_spreads[i + 1].spread - s.spread > 0 ? 'text-green-600' : data.recent_spreads[i + 1].spread - s.spread < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {data.recent_spreads[i + 1].spread - s.spread > 0 ? '+' : ''}{data.recent_spreads[i + 1].spread - s.spread}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Tabs defaultValue="spread-freq">
              <TabsList className="mb-4">
                <TabsTrigger value="spread-freq" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-spread-freq">
                  Spread Frequency (1-11)
                </TabsTrigger>
                <TabsTrigger value="spread-diff" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-spread-diff">
                  Spread-to-Spread Difference
                </TabsTrigger>
              </TabsList>

              <TabsContent value="spread-freq">
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-indigo-600 to-blue-700 text-white">
                    <h3 className="font-bold">Spread Frequency — LS2 - LS1 (1 to 11)</h3>
                    <p className="text-[10px] text-blue-200 mt-0.5">How often each spread value appears. Current spread: {data.last_draw.spread}</p>
                  </div>
                  <SpreadTable rows={data.spread_numbers} type="spread" data={data} />
                </div>
              </TabsContent>

              <TabsContent value="spread-diff">
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-rose-600 to-red-700 text-white">
                    <h3 className="font-bold">Spread-to-Spread Difference — from current spread {data.last_draw.spread}</h3>
                    <p className="text-[10px] text-red-200 mt-0.5">If diff = +2 and current spread = {data.last_draw.spread}, next spread = {(data.last_draw.spread || 0) + 2}. Pred column shows predicted next spread.</p>
                  </div>
                  <SpreadTable rows={data.diff_numbers} type="diff" data={data} />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : null}
      </main>
    </div>
  );
}
